import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Clock3Icon from "@mui/icons-material/AccessTime";
import MessageSquarePlusIcon from "@mui/icons-material/AddComment";
import BarChartIcon from "@mui/icons-material/BarChart";
import Code2Icon from "@mui/icons-material/Code";
import PanelLeftIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExternalLinkIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ThumbsDownIcon from "@mui/icons-material/ThumbDown";
import ThumbsUpIcon from "@mui/icons-material/ThumbUp";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { strToU8, zipSync } from "fflate";
import ReactECharts from "echarts-for-react";
import { MockApi, UNAUTHORIZED_EVENT } from "./api/mockApi";
import { useAiConsent } from "./AiConsentGate";
import {
  AI_CONSENT_POLICY_LINKS,
  AI_CONSENT_PROVIDER,
  formatProviderDisplayName,
  isConsentCheckCandidate
} from "./consentConstants";
import RequestCard from "./RequestCard";
import aiChatIcon from "../assets/img/AI_Chat_Assistant_Icon.svg";
import AiChatBrand from "./AiChatBrand";

import  "./styles.css";
import appConfig from "../config";
import { useSelector } from "react-redux";
import { canManageTokens as hasTokenAdminAccess } from "./tokenAccess";
import { RoleBaseAccess } from "../hoc";
import { trackActivity } from "../utils/mixpanel";

const RECHARGE_DEFAULT_PAGE_SIZE = 10;
const RECHARGE_PAGE_SIZE_OPTIONS = [10, 20, 50];
const COMMENTS_MAX = 1000;

function sizedIcon(Icon) {
  return function SizedIcon({ size, sx, ...props }) {
    return <Icon {...props} sx={{ fontSize: size, ...sx }} />;
  };
}

const BarChartToggleIcon = sizedIcon(BarChartIcon);
const Clock3 = sizedIcon(Clock3Icon);
const Code2 = sizedIcon(Code2Icon);
const Download = sizedIcon(DownloadIcon);
const ExternalLink = sizedIcon(ExternalLinkIcon);
const MessageSquarePlus = sizedIcon(MessageSquarePlusIcon);
const PanelLeft = sizedIcon(PanelLeftIcon);
const Refresh = sizedIcon(RefreshIcon);
const Search = sizedIcon(SearchIcon);
const Send = sizedIcon(SendIcon);
const ThumbsDown = sizedIcon(ThumbsDownIcon);
const ThumbsUp = sizedIcon(ThumbsUpIcon);

function Loader2({ size, ...props }) {
  return <CircularProgress {...props} size={size} />;
}

const exampleQuestions = [
  "Total no. of contracts created in last 12 month, monthwise, table format",
  "Total no. of contracts created only in 2025-06",
  "List invoices overdue for 30 days or more",
  "Give lease with ref no. asset details"
];

/** Sentence-case: uppercase the first letter (standard question display). */
function capitalizeQuestion(text) {
  const value = String(text ?? "");
  const match = value.match(/\S/);
  if (!match) return value;
  const index = match.index;
  return (
    value.slice(0, index) +
    value.charAt(index).toUpperCase() +
    value.slice(index + 1)
  );
}

function formatQuestionError(error) {
  const detail = String(error?.data?.detail || error?.message || "Unable to send the question.")
    .replace(/^Error executing tool [\w-]+:\s*/i, "")
    .trim();
  if (/^Lease not found for the authenticated lessor\.?$/i.test(detail)) {
    return "I couldn't find that lease for your account. Check the lease reference and try again.";
  }
  return detail || "Unable to send the question.";
}

const rawConsoleUrl =  appConfig.domain.subDomian;
const CONSOLE_URL = (
  /^https?:\/\//i.test(rawConsoleUrl) ? rawConsoleUrl : `https://${rawConsoleUrl}`
).replace(/\/+$/, "");
const LOGIN_URL = `${CONSOLE_URL}/login`;

function UnauthorizedOverlay() {
  return (
    <div className="unauthorized-overlay" role="alertdialog" aria-modal="true">
      <section className="unauthorized-card">
        <h2>Unauthorized Access</h2>
        <p>Your session is not authorized to access this application.</p>
        <a className="unauthorized-login" href={LOGIN_URL}>
          Login
        </a>
      </section>
    </div>
  );
}

function formatDate(value) {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function normalizeTokens(tokens) {
  if (!tokens) return null;
  const input = Number(tokens.input ?? tokens.input_tokens ?? 0);
  const output = Number(tokens.output ?? tokens.output_tokens ?? 0);
  const total = Number(tokens.total ?? tokens.sent_tokens ?? input + output);
  const retries = Number(tokens.retries ?? 0);
  return { input, output, total, retries };
}

function TokenSummary({ tokens, compact = false }) {
  const normalized = normalizeTokens(tokens);
  if (!normalized) return null;

  return (
    <div className={compact ? "token-summary compact" : "token-summary"}>
      <span>Tokens</span>
      <b>Input: {normalized.input.toLocaleString()}</b>
      <b>Output: {normalized.output.toLocaleString()}</b>
      <b>Total: {normalized.total.toLocaleString()}</b>
    </div>
  );
}

function normalizeTokenUsage(usage) {
  if (!usage) return null;
  const alloted = Number(usage.alloted_tokens ?? usage.allotted_tokens ?? 0);
  const used = Number(usage.used_tokens ?? 0);
  const available = Number(usage.available_tokens ?? Math.max(alloted - used, 0));
  return {
    alloted: Math.max(alloted, 0),
    used: Math.max(used, 0),
    available: Math.max(available, 0)
  };
}

function normalizeRechargePagination(history, fallbackPage = 1, fallbackPageSize = 10) {
  const nested = history?.pagination || {};
  const page = Number(history?.page ?? nested.page ?? fallbackPage) || fallbackPage;
  const pageSize = Number(
    history?.page_size ?? nested.per_page ?? nested.page_size ?? fallbackPageSize
  ) || fallbackPageSize;
  const count = Number(history?.count ?? nested.total ?? nested.count ?? 0) || 0;
  const reportedTotalPages = Number(history?.total_pages ?? nested.total_pages) || 0;
  const computedTotalPages = count > 0 ? Math.ceil(count / pageSize) : 1;
  const totalPages = Math.max(1, reportedTotalPages, computedTotalPages);
  return {
    page,
    total_pages: totalPages,
    count,
    page_size: pageSize
  };
}

function UserTokenUsage({ usage, onRecharge, canRecharge = false }) {
  const normalized = normalizeTokenUsage(usage);
  if (!normalized) return null;
  const consumedPercent = normalized.alloted
    ? Math.min((normalized.used / normalized.alloted) * 100, 100)
    : 100;

  return (
    <div className={`user-token-usage ${normalized.available < 1 ? "exhausted" : ""}`}>
      <div className="usage-heading">
        <span>Account&apos;s Token Balance</span>
        <strong>{normalized.available.toLocaleString()} left</strong>
      </div>
      <div
        className="usage-progress"
        role="progressbar"
        aria-label="Consumed tokens"
        aria-valuemin="0"
        aria-valuemax={normalized.alloted}
        aria-valuenow={Math.min(normalized.used, normalized.alloted)}
      >
        <span style={{ width: `${consumedPercent}%` }} />
      </div>
      <div className="usage-values">
        <span>{normalized.used.toLocaleString()} consumed</span>
        <span>{normalized.alloted.toLocaleString()} allotted</span>
      </div>
      {canRecharge ? (
        <button type="button" className="recharge-button" onClick={onRecharge}>
          Recharge Tokens
        </button>
      ) : null}
    </div>
  );
}

function RechargeModal({
  open,
  usage,
  packages,
  requests,
  pagination,
  confirmation,
  submitting,
  loading,
  error,
  cancellingRequestId,
  confirmDisabled = false,
  onConfirm,
  onCancelRequest,
  onPageChange,
  onPageSizeChange,
  onClose
}) {
  const [selectedTokens, setSelectedTokens] = useState(null);
  const [requestComments, setRequestComments] = useState("");
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [cancelComments, setCancelComments] = useState("");
  const [cancelError, setCancelError] = useState("");
  const normalized = normalizeTokenUsage(usage);

  useEffect(() => {
    if (!open) {
      setSelectedTokens(null);
      setRequestComments("");
      setCancelTargetId(null);
      setCancelComments("");
      setCancelError("");
    }
  }, [open]);

  async function submitCancellation() {
    const comments = cancelComments.trim();
    if (!cancelTargetId || !comments || cancellingRequestId) return;

    setCancelError("");
    try {
      await onCancelRequest(cancelTargetId, comments);
      setCancelTargetId(null);
      setCancelComments("");
    } catch (requestError) {
      setCancelError(
        requestError.message || "Unable to cancel the token request."
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="recharge-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 3 },
      }}
    >
      <Box
        className="recharge-modal"
        component="section"
        tabIndex={-1}
        sx={{ outline: "none" }}
      >
        <div className={`modal-header${confirmation ? " modal-header--confirmation" : ""}`}>
          {confirmation ? (
            <div className="confirmation-header">
              <span className="modal-eyebrow">Token top-up</span>
              <h2 id="recharge-title">Request received!</h2>
              <div className="success-mark" aria-hidden="true">
                ✓
              </div>
            </div>
          ) : (
            <div>
              <span className="modal-eyebrow">Token top-up</span>
              <h2 id="recharge-title">Recharge Tokens</h2>
            </div>
          )}
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="recharge-modal-body">
        {confirmation ? (
          <div className="request-confirmation" >
            <p style={{textAlign:'center'}}>
              Your token top-up is being processed. Tokens will be added to your
              account within <strong>24 hours</strong> of working day.
            </p>
            <div className="billing-note">
              <span>Billing note</span>
              <strong>
                ${Number(confirmation.amount_usd).toLocaleString()} for{" "}
                {Number(confirmation.token_amount).toLocaleString()} tokens will be
                added to your {confirmation.billing_period} invoice.
              </strong>
            </div>
          </div>
        ) : (
          <>
            <div className="current-balance">
              <span>Current Balance</span>
              <strong>{(normalized?.available || 0).toLocaleString()} tokens</strong>
            </div>

            <h3>Select a Token Package</h3>
            <div className="package-grid">
              {packages.map((item) => (
                <button
                  type="button"
                  key={item.token_amount}
                  className={`package-option ${
                    selectedTokens === item.token_amount ? "selected" : ""
                  } ${item.most_popular ? "popular" : ""}`}
                  onClick={() => setSelectedTokens(item.token_amount)}
                >
                  {item.most_popular && (
                    <span className="package-popular-badge">Most Popular</span>
                  )}
                  <span className="package-tokens">
                    {Number(item.token_amount).toLocaleString()} tokens
                  </span>
                  <span className="package-price">
                    <strong>${Number(item.amount_usd).toLocaleString()}</strong> / one-time
                  </span>
                  <small>
                    Good for ~{Number(item.estimated_queries).toLocaleString()} queries
                  </small>
                </button>
              ))}
            </div>
            {loading && <div className="modal-loading">Loading token packages…</div>}

            {error && <div className="recharge-error" role="alert">{error}</div>}

            <div className="recharge-request-comments">
              <label htmlFor="recharge-request-comments">
                Comments <span className="required-mark">*</span>
              </label>
              <textarea
                id="recharge-request-comments"
                value={requestComments}
                onChange={(event) =>
                  setRequestComments(
                    event.target.value.slice(0, COMMENTS_MAX)
                  )
                }
                placeholder="Enter comments for this token request"
                maxLength={COMMENTS_MAX}
                rows={3}
                required
              />
              <div className="recharge-request-comments-count">
                {requestComments.length}/{COMMENTS_MAX}
              </div>
            </div>

            <div className="modal-actions recharge-modal-actions">
              {confirmDisabled ? (
                <p className="recharge-beta-note">
                  <InfoOutlinedIcon className="recharge-beta-note-icon" />
                  Requests cannot be raised in the beta version.
                </p>
              ) : null}
              <div className="modal-actions-buttons">
                <button type="button" className="secondary-action" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-action"
                  disabled={
                    confirmDisabled ||
                    !selectedTokens ||
                    !requestComments.trim() ||
                    submitting
                  }
                  onClick={() => onConfirm(selectedTokens, requestComments.trim())}
                >
                  {submitting ? "Submitting…" : "Confirm Request"}
                </button>
              </div>
            </div>
          </>
        )}

        <div className="request-history">
          <h3>Previous requests</h3>
          {loading ? (
            <div className="modal-loading">Loading previous requests…</div>
          ) : requests.length ? (
            <div className="request-list">
              {requests.map((item) => (
                <RequestCard
                  key={item.id}
                  item={item}
                  actions={
                    String(item.status).toLowerCase() === "pending" &&
                    cancelTargetId !== item.id ? (
                      <button
                        type="button"
                        className="cancel-request-button"
                        disabled={Boolean(cancellingRequestId)}
                        onClick={() => {
                          setCancelTargetId(item.id);
                          setCancelComments("");
                          setCancelError("");
                        }}
                      >
                        Cancel request
                      </button>
                    ) : null
                  }
                  form={
                    cancelTargetId === item.id ? (
                      <div className="cancel-request-form">
                        <label htmlFor={`cancel-comments-${item.id}`}>
                          Cancellation comments
                        </label>
                        <textarea
                          id={`cancel-comments-${item.id}`}
                          value={cancelComments}
                          onChange={(event) =>
                            setCancelComments(event.target.value)
                          }
                          placeholder="Enter the reason for cancelling this request"
                          maxLength={COMMENTS_MAX}
                          rows={3}
                        />
                        {cancelError && (
                          <div className="cancel-request-error" role="alert">
                            {cancelError}
                          </div>
                        )}
                        <div className="cancel-request-actions">
                          <button
                            type="button"
                            className="secondary-action"
                            disabled={Boolean(cancellingRequestId)}
                            onClick={() => {
                              setCancelTargetId(null);
                              setCancelComments("");
                              setCancelError("");
                            }}
                          >
                            Keep request
                          </button>
                          <button
                            type="button"
                            className="cancel-confirm-button"
                            disabled={
                              !cancelComments.trim() ||
                              Boolean(cancellingRequestId)
                            }
                            onClick={submitCancellation}
                          >
                            {cancellingRequestId === item.id
                              ? "Cancelling…"
                              : "Confirm cancellation"}
                          </button>
                        </div>
                      </div>
                    ) : null
                  }
                />
              ))}
            </div>
          ) : (
            <p className="empty-history">No token recharge requests yet.</p>
          )}
          {(Number(pagination?.count || 0) > 0 || Number(pagination?.total_pages || 0) > 1) && (
            <div className="request-pagination">
              <div className="page-size-control">
                <span className="page-size-label">Items per page</span>
                <Select
                  size="small"
                  className="page-size-select"
                  value={Number(pagination?.page_size || RECHARGE_DEFAULT_PAGE_SIZE)}
                  disabled={loading}
                  onChange={(event) => {
                    const nextPageSize =
                      Number(event.target.value) || RECHARGE_DEFAULT_PAGE_SIZE;
                    onPageSizeChange?.(nextPageSize);
                  }}
                  MenuProps={{
                    PaperProps: { className: "page-size-menu-paper" }
                  }}
                >
                  {RECHARGE_PAGE_SIZE_OPTIONS.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="request-pagination-nav">
                <button
                  type="button"
                  disabled={loading || Number(pagination.page || 1) <= 1}
                  onClick={() => onPageChange(Number(pagination.page || 1) - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {Number(pagination.page || 1)} of {Number(pagination.total_pages || 1)}
                </span>
                <button
                  type="button"
                  disabled={
                    loading ||
                    Number(pagination.page || 1) >= Number(pagination.total_pages || 1)
                  }
                  onClick={() => onPageChange(Number(pagination.page || 1) + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {confirmation && (
          <div className="modal-actions confirmation-actions">
            <button type="button" className="primary-action" onClick={onClose}>
              Done
            </button>
          </div>
        )}
        </div>
      </Box>
    </Modal>
  );
}

function getFeedbackRating(message) {
  const value = message?.feedback;
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.feedback || value.rating || "";
}

function getFeedbackComments(message) {
  if (!message) return "";
  const feedback = message.feedback;
  if (feedback && typeof feedback === "object") {
    const nested =
      feedback.comments ?? feedback.comment ?? feedback.feedback_comments;
    if (nested) return String(nested).trim();
  }
  return String(
    message.comments ??
      message.feedback_comments ??
      message.feedback_comment ??
      message.comment ??
      ""
  ).trim();
}

function mergeFeedbackIntoThread(thread, messageId, { rating, comments } = {}) {
  if (!thread?.messages?.length) return thread;
  return {
    ...thread,
    messages: thread.messages.map((message) =>
      message.id === messageId
        ? {
            ...message,
            feedback: rating || getFeedbackRating(message),
            comments: comments ?? getFeedbackComments(message)
          }
        : message
    )
  };
}

function FeedbackCommentModal({
  submitting,
  initialComments = "",
  onSubmit,
  onClose
}) {
  const [comments, setComments] = useState(initialComments || "");

  useEffect(() => {
    setComments(initialComments || "");
  }, [initialComments]);

  function handleSubmit(event) {
    event.preventDefault();
    const value = comments.trim();
    if (value) onSubmit(value);
  }

  return (
    <Modal
      open
      onClose={onClose}
      aria-labelledby="feedback-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 3 },
      }}
    >
      <Box
        className="feedback-modal"
        component="form"
        tabIndex={-1}
        onSubmit={handleSubmit}
        sx={{ outline: "none" }}
      >
        <div className="modal-header">
          <div>
            <span className="modal-eyebrow">Answer feedback</span>
            <h2 id="feedback-title">What was not useful?</h2>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="feedback-help">
          Your comments help the AI Chat Assistant provide better answers.
        </p>
        <textarea
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          placeholder="Tell us what was incorrect or missing"
          maxLength={COMMENTS_MAX}
          autoFocus
          required
        />
        <div className="modal-actions">
          <button type="button" className="secondary-action" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary-action"
            disabled={!comments.trim() || submitting}
          >
            {submitting ? "Saving…" : "Submit feedback"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

function safeFileName(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "lease-ai-results";
}

function downloadBlob(content, fileName, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}



function columnName(index) {
  let value = index + 1;
  let name = "";
  while (value > 0) {
    value -= 1;
    name = String.fromCharCode(65 + (value % 26)) + name;
    value = Math.floor(value / 26);
  }
  return name;
}

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function buildExcelWorkbook(rows) {
  const columnCount = Math.max(0, ...rows.map((row) => row.length));
  const columns = Array.from({ length: columnCount }, (_, columnIndex) => {
    const contentWidth = Math.max(
      0,
      ...rows.map((row) => String(row[columnIndex] ?? "").length)
    );
    const width = Math.min(Math.max(contentWidth + 2, 10), 40);
    return `<col min="${columnIndex + 1}" max="${columnIndex + 1}" width="${width}" customWidth="1"/>`;
  }).join("");
  const sheetRows = rows
    .map((row, rowIndex) => {
      const rowHasData = row.some(
        (cell) =>
          cell !== null &&
          cell !== undefined &&
          String(cell).trim() !== ""
      );
      const cells = Array.from({ length: columnCount }, (_, columnIndex) => {
        const cell = row[columnIndex] ?? "";
        const styleIndex = rowIndex === 0 ? 1 : rowHasData ? 2 : 0;
        const styleAttribute = styleIndex ? ` s="${styleIndex}"` : "";
        return `<c r="${columnName(columnIndex)}${rowIndex + 1}"${styleAttribute} t="inlineStr"><is><t xml:space="preserve">${escapeXml(cell)}</t></is></c>`;
      })
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");
  const files = {
    "[Content_Types].xml": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`
    ),
    "_rels/.rels": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`
    ),
    "xl/workbook.xml": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Results" sheetId="1" r:id="rId1"/></sheets></workbook>`
    ),
    "xl/_rels/workbook.xml.rels": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`
    ),
    "xl/styles.xml": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font><font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/><family val="2"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFADD8E6"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left style="thin"><color rgb="FF000000"/></left><right style="thin"><color rgb="FF000000"/></right><top style="thin"><color rgb="FF000000"/></top><bottom style="thin"><color rgb="FF000000"/></bottom><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf><xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`
    ),
    "xl/worksheets/sheet1.xml": strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${columns}</cols><sheetData>${sheetRows}</sheetData></worksheet>`
    )
  };
  return zipSync(files, { level: 6 });
}

function escapeCsvCell(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}


function downloadCsv(rows, fileName) {
  const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
  downloadBlob(csv, `${fileName}.csv`, "text/csv;charset=utf-8");
}

function downloadExcel(rows, fileName) {
   downloadBlob(
    buildExcelWorkbook(rows),
    `${fileName}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function rowValue(row, headerMap, candidates) {
  for (const key of candidates) {
    const normalizedKey = normalizeHeader(key);
    const index = headerMap[normalizedKey];

    if (index !== undefined) {
      const value = row[index];

      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
    }
  }

  return "";
}



function assetTypeName(value) {
  const normalized = String(value || "").trim().toLowerCase();

  const assetTypeMap = {
    "1": "aircraft",
    "aircraft": "aircraft",
    "msn": "aircraft",

    "2": "engine",
    "engine": "engine",
    "esn": "engine",

    "3": "apu",
    "apu": "apu",

    "4": "landing-gear",
    "lg": "landing-gear",
    "landing gear": "landing-gear",

    "5": "propeller",
    "propeller": "propeller",

    "6": "fan",
    "fan": "fan",

    "7": "hpc",
    "hpc": "hpc",

    "8": "hpt",
    "hpt": "hpt",

    "9": "lpt",
    "lpt": "lpt",

    "10": "component",
    "component": "component"
  };

  return assetTypeMap[normalized] || null;
}

function buildAssetHref(row, headerMap) {
  const assetSlug = rowValue(row, headerMap, [
    "asset_slug",
    "asset__slug"
  ]);

  const assetTypeValue = rowValue(row, headerMap, [
    "asset__asset_type_name",
    "asset_type_name",
    "asset_type_label",
    "asset_type",
    "asset_type_id"
  ]);

  const assetNameValue = rowValue(row, headerMap, [
    "asset__asset_name",
    "asset_name"
  ]);

  const type = assetTypeName(assetTypeValue);
  const assetName = assetNameValue ? String(assetNameValue).trim() : "";

  if (!assetSlug || !type || !assetName) return null;

  return `${CONSOLE_URL}/technical-specification/${encodeURIComponent(type)}/${encodeURIComponent(assetSlug)}/basic-details`;
}
function buildSlugHref(header, row, headerMap) {
  const name = header.normalized;
  const slug = String(row[header.index] || "").trim();

  if (!slug) return null;

  if (name === "asset_slug" || name.includes("asset_slug")) {
    return buildAssetHref(row, headerMap);
  }

  if (name.includes("invoice") && name.includes("slug")) {
    return `/invoices/view/${encodeURIComponent(slug)}`;
  }

  if (((name.includes("contract") || name.includes("lease")) && name.includes("slug")) || name === "slug") {
    return `/lease/${encodeURIComponent(slug)}/lease-details/headlines`;
  }

  return null;
}

function buildRelevantHref(header, row, headerMap) {
  const name = header.normalized;

  if (
    name === "asset_name" ||
    name === "asset__asset_name" ||
    name === "asset_type_name" ||
    name === "asset_type_label" ||
    name === "asset__asset_type_name"
  ) {
    return buildAssetHref(row, headerMap);
  }

  if (name === "invoice_number" || name === "invoice_no" || name === "invoice") {
    const invoiceSlug = rowValue(row, headerMap, ["invoice_slug", "slug"]);
    if (invoiceSlug) return `/invoices/view/${encodeURIComponent(invoiceSlug)}`;
  }

  if (
    name === "reference_code" ||
    name === "reference_number" ||
    name === "reference_no" ||
    name === "contract_number" ||
    name === "contract_reference" ||
    name === "lease_reference"
  ) {
    const contractSlug = rowValue(row, headerMap, ["contract_slug", "lease_slug", "slug"]);
    if (contractSlug) return `/lease/${encodeURIComponent(contractSlug)}/lease-details/headlines`;
  }

  return null;
}

function isHiddenTableColumn(header) {
  const name = header.normalized;
  return (
    name === "id" ||
    name === "unique_identifier" ||
    name === "slug" ||
    name.endsWith("_id") ||
    name.endsWith("__id") ||
    name.endsWith("_slug")
  );
}

function isClosingBalanceRow(row, visibleHeaders) {
  const firstHeader = visibleHeaders[0];
  if (!firstHeader) return false;
  return String(row[firstHeader.index] ?? "").trim().toLowerCase() === "closing balance";
}

function LinkedTableCell({ cell, row, header, headerMap }) {
  const href = buildRelevantHref(header, row, headerMap) || buildSlugHref(header, row, headerMap);
  if (!href) return cell;

  return (
    <a className="table-link" href={href} target="_blank" rel="noreferrer" title="Open in new window">
      <span>{cell}</span>
      <ExternalLink size={13} />
    </a>
  );
}
function formatHeaderLabel(value) {
  const words = String(value || "")
    .replace(/__/g, "_")
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/);

  const cleanedWords = words.filter((word, index) => {
    if (index === 0) return true;
    return word.toLowerCase() !== words[index - 1].toLowerCase();
  });

  return cleanedWords
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function parseNumericCell(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text) return null;
  const negative = /^\(.*\)$/.test(text);
  const cleaned = text.replace(/[()$,%\s]/g, "");
  if (!cleaned || !/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return null;
  return negative ? -Math.abs(num) : num;
}

function isNumericColumn(dataRows, index) {
  let numericCount = 0;
  let total = 0;
  for (const row of dataRows) {
    const raw = row[index];
    if (raw === null || raw === undefined || String(raw).trim() === "") continue;
    total += 1;
    if (parseNumericCell(raw) !== null) numericCount += 1;
  }
  return total > 0 && numericCount / total >= 0.8;
}

const CHART_SERIES_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#4a3aa7",
  "#e34948"
];
const MAX_CHART_ROWS = 100;

function ChartPanel({ headers, dataRows }) {
  const numericHeaders = useMemo(
    () => headers.filter((header) => isNumericColumn(dataRows, header.index)),
    [headers, dataRows]
  );
  const [chartType, setChartType] = useState("bar");
  const [xIndex, setXIndex] = useState(headers[0]?.index ?? 0);
  const [yIndices, setYIndices] = useState(
    numericHeaders[0] ? [numericHeaders[0].index] : []
  );

  const truncated = dataRows.length > MAX_CHART_ROWS;
  const chartRows = truncated ? dataRows.slice(0, MAX_CHART_ROWS) : dataRows;
  const yHeaders = yIndices
    .map((index) => numericHeaders.find((header) => header.index === index))
    .filter(Boolean);

  const chartOption = useMemo(() => {
    if (!yHeaders.length) return null;

    const categories = chartRows.map(
      (row, rowIndex) => String(row[xIndex] ?? `Row ${rowIndex + 1}`)
    );
    const seriesValues = yHeaders.map((header) =>
      chartRows.map((row) => parseNumericCell(row[header.index]) ?? 0)
    );

    if (chartType === "pie") {
      const pieData = categories.map((name, index) => ({
        name,
        value: seriesValues[0]?.[index] ?? 0
      }));
      return {
        color: CHART_SERIES_COLORS,
        tooltip: { trigger: "item" },
        legend: { type: "scroll", bottom: 0 },
        series: [
          {
            type: "pie",
            radius: "65%",
            data: pieData,
            label: { formatter: "{b}" }
          }
        ]
      };
    }

    return {
      color: CHART_SERIES_COLORS,
      tooltip: { trigger: "axis" },
      legend: yHeaders.length > 1 ? { type: "scroll", bottom: 0 } : undefined,
      grid: {
        left: 48,
        right: 24,
        top: 24,
        bottom: yHeaders.length > 1 ? 56 : 40
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: { fontSize: 11, hideOverlap: true }
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11 }
      },
      series: yHeaders.map((header, seriesIndex) => ({
        name: formatHeaderLabel(header.label),
        type: chartType === "line" ? "line" : "bar",
        data: seriesValues[seriesIndex],
        smooth: chartType === "line",
        showSymbol: chartType === "line",
        symbolSize: 6,
        itemStyle:
          chartType === "bar"
            ? { borderRadius: [4, 4, 0, 0] }
            : undefined
      }))
    };
  }, [chartRows, chartType, xIndex, yHeaders]);

  if (!numericHeaders.length) {
    return (
      <div className="chart-panel">
        <div className="chart-empty">No numeric columns were found to chart.</div>
      </div>
    );
  }

  function handleChartTypeChange(nextType) {
    setChartType(nextType);
    if (nextType === "pie" && yIndices.length > 1) {
      setYIndices([yIndices[0]]);
    }
  }

  function toggleYIndex(index) {
    setYIndices((current) => {
      if (chartType === "pie") return [index];
      if (current.includes(index)) {
        return current.length > 1 ? current.filter((item) => item !== index) : current;
      }
      return [...current, index];
    });
  }

  return (
    <div className="chart-panel">
      <div className="chart-controls">
        <label className="chart-control">
          Chart type
          <select value={chartType} onChange={(event) => handleChartTypeChange(event.target.value)}>
            <option value="bar">Bar</option>
            <option value="line">Line</option>
            <option value="pie">Pie</option>
          </select>
        </label>
        <label className="chart-control">
          X axis
          <select value={xIndex} onChange={(event) => setXIndex(Number(event.target.value))}>
            {headers.map((header) => (
              <option key={header.index} value={header.index}>
                {formatHeaderLabel(header.label)}
              </option>
            ))}
          </select>
        </label>
        <div className="chart-control chart-y-picker">
          <span>{chartType === "pie" ? "Value" : "Y axis"}</span>
          <div className="chart-y-options">
            {numericHeaders.map((header) => (
              <label key={header.index} className="chart-y-option">
                <input
                  type={chartType === "pie" ? "radio" : "checkbox"}
                  name="chart-y-axis"
                  checked={yIndices.includes(header.index)}
                  onChange={() => toggleYIndex(header.index)}
                />
                {formatHeaderLabel(header.label)}
              </label>
            ))}
          </div>
        </div>
      </div>
      {truncated && (
        <div className="chart-note">
          Showing the first {MAX_CHART_ROWS} of {dataRows.length} rows.
        </div>
      )}
      {yHeaders.length === 0 || !chartOption ? (
        <div className="chart-empty">Select at least one column to chart.</div>
      ) : (
        <div className="chart-canvas">
          <ReactECharts
            option={chartOption}
            style={{
              height: chartRows.length <= 3 ? 220 : 320,
              width: "100%"
            }}
            notMerge
            lazyUpdate={false}
          />
        </div>
      )}
    </div>
  );
}

function compareCells(a, b, numeric) {
  if (numeric) {
    const na = parseNumericCell(a);
    const nb = parseNumericCell(b);
    if (na === null && nb === null) return 0;
    if (na === null) return 1;
    if (nb === null) return -1;
    return na - nb;
  }
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base"
  });
}

function getDistinctColumnValues(dataRows, index) {
  const seen = new Set();
  for (const row of dataRows) {
    const raw = row[index];
    seen.add(raw === null || raw === undefined ? "" : String(raw));
  }
  return Array.from(seen).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
}

const COLUMN_FILTER_MENU_WIDTH = 220;
const COLUMN_FILTER_MENU_MAX_HEIGHT = 320;

function clampColumnFilterMenuPosition(anchorRect) {
  const margin = 8;
  let left = anchorRect.left;
  left = Math.min(left, window.innerWidth - COLUMN_FILTER_MENU_WIDTH - margin);
  left = Math.max(margin, left);

  let top = anchorRect.bottom + 4;
  const fitsBelow = top + COLUMN_FILTER_MENU_MAX_HEIGHT <= window.innerHeight - margin;
  if (!fitsBelow && anchorRect.top - COLUMN_FILTER_MENU_MAX_HEIGHT - 4 >= margin) {
    top = anchorRect.top - COLUMN_FILTER_MENU_MAX_HEIGHT - 4;
  }
  return { top, left };
}

function ColumnFilterMenu({ header, allDataRows, selectedValues, position, onChange, onClear, onClose }) {
  const [search, setSearch] = useState("");
  const distinctValues = useMemo(
    () => getDistinctColumnValues(allDataRows, header.index),
    [allDataRows, header.index]
  );
  const activeSet = selectedValues || new Set(distinctValues);
  const filteredValues = search.trim()
    ? distinctValues.filter((value) => value.toLowerCase().includes(search.trim().toLowerCase()))
    : distinctValues;
  const allFilteredChecked = filteredValues.length > 0 && filteredValues.every((value) => activeSet.has(value));

  function toggleValue(value) {
    const next = new Set(activeSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next, distinctValues);
  }

  function toggleSelectAllFiltered() {
    const next = new Set(activeSet);
    if (allFilteredChecked) {
      filteredValues.forEach((value) => next.delete(value));
    } else {
      filteredValues.forEach((value) => next.add(value));
    }
    onChange(next, distinctValues);
  }

  return createPortal(
    <>
      <div className="column-filter-overlay" onClick={onClose} />
      <div
        className="column-filter-menu"
        style={{ top: position.top, left: position.left }}
        onClick={(event) => event.stopPropagation()}
      >
        <input
          type="text"
          className="column-filter-search"
          placeholder="Search values"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoFocus
        />
        <label className="column-filter-option column-filter-select-all">
          <input type="checkbox" checked={allFilteredChecked} onChange={toggleSelectAllFiltered} />
          (Select All)
        </label>
        <div className="column-filter-values">
          {filteredValues.length === 0 && <div className="column-filter-empty">No values</div>}
          {filteredValues.map((value) => (
            <label key={value} className="column-filter-option">
              <input type="checkbox" checked={activeSet.has(value)} onChange={() => toggleValue(value)} />
              {value === "" ? "(Blank)" : value}
            </label>
          ))}
        </div>
        <div className="column-filter-footer">
          <button type="button" onClick={onClear}>
            Clear
          </button>
          <button type="button" className="primary" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function ResultTable({ rows, tableIndex }) {
  const [chartOpen, setChartOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortState, setSortState] = useState(null);
  const [openFilterIndex, setOpenFilterIndex] = useState(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (openFilterIndex === null) return undefined;
    function closeMenu() {
      setOpenFilterIndex(null);
      setFilterMenuPosition(null);
    }
    function handleScroll(event) {
      // Keep the menu open while scrolling its own value list.
      if (event.target?.closest?.(".column-filter-menu")) return;
      closeMenu();
    }
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [openFilterIndex]);

  const fileName = safeFileName(`lease-ai-table-${tableIndex + 1}`);
  const headerRow = rows[0] || [];
  const headers = headerRow.map((label, index) => ({ label, index, normalized: normalizeHeader(label) }));
  const headerMap = headers.reduce((map, header) => {
    map[header.normalized] = header.index;
    return map;
  }, {});
  const baseVisibleHeaders = headers.filter((header) => !isHiddenTableColumn(header));
  const allDataRows = rows.length ? rows.slice(1) : [];

  const [columnOrder, setColumnOrder] = useState(() => baseVisibleHeaders.map((header) => header.index));
  const visibleHeaders = useMemo(() => {
    const byIndex = new Map(baseVisibleHeaders.map((header) => [header.index, header]));
    const ordered = columnOrder.map((index) => byIndex.get(index)).filter(Boolean);
    const missing = baseVisibleHeaders.filter((header) => !columnOrder.includes(header.index));
    return [...ordered, ...missing];
  }, [columnOrder, baseVisibleHeaders]);

  const numericColumns = useMemo(
    () =>
      new Set(
        baseVisibleHeaders
          .filter((header) => isNumericColumn(allDataRows, header.index))
          .map((header) => header.index)
      ),
    [baseVisibleHeaders, allDataRows]
  );

  const filteredRows = useMemo(() => {
    const activeEntries = Object.entries(columnFilters);
    if (!activeEntries.length) return allDataRows;
    return allDataRows.filter((row) =>
      activeEntries.every(([index, allowedValues]) => {
        const raw = row[Number(index)];
        const text = raw === null || raw === undefined ? "" : String(raw);
        return allowedValues.has(text);
      })
    );
  }, [allDataRows, columnFilters]);

  const dataRows = useMemo(() => {
    if (!sortState) return filteredRows;
    const numeric = numericColumns.has(sortState.index);
    const sorted = [...filteredRows].sort((a, b) => compareCells(a[sortState.index], b[sortState.index], numeric));
    if (sortState.direction === "desc") sorted.reverse();
    return sorted;
  }, [filteredRows, sortState, numericColumns]);

  if (!rows.length) return null;

  const exportRows = [
    visibleHeaders.map((header) => formatHeaderLabel(header.label)),
    ...dataRows.map((row) => visibleHeaders.map((header) => row[header.index] ?? ""))
  ];
  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  function handleSortClick(index) {
    setSortState((current) => {
      if (!current || current.index !== index) return { index, direction: "asc" };
      if (current.direction === "asc") return { index, direction: "desc" };
      return null;
    });
  }

  function handleFilterChange(index, nextSet, distinctValues) {
    setColumnFilters((current) => {
      const next = { ...current };
      if (nextSet.size >= distinctValues.length) {
        delete next[index];
      } else {
        next[index] = nextSet;
      }
      return next;
    });
  }

  function handleFilterClear(index) {
    setColumnFilters((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
  }

  function handleColumnDrop(sourceIndex, targetIndex) {
    setColumnOrder((current) => {
      if (sourceIndex === null || Number.isNaN(sourceIndex) || sourceIndex === targetIndex) return current;
      const order = [...current];
      const from = order.indexOf(sourceIndex);
      const to = order.indexOf(targetIndex);
      if (from === -1 || to === -1) return current;
      order.splice(from, 1);
      order.splice(to, 0, sourceIndex);
      return order;
    });
    setDragIndex(null);
  }

  return (
    <div className="result-table-wrap">
      <div className="table-toolbar">
        <span className="table-toolbar-count">
          {dataRows.length} of {allDataRows.length} rows
          {hasActiveFilters ? " (filtered)" : ""}
        </span>
        <div className="table-actions">
          {hasActiveFilters && (
            <button
              type="button"
              className="table-action-clear"
              onClick={() => setColumnFilters({})}
              title="Clear column filters"
            >
              Clear filters
            </button>
          )}
          <div className="table-action-group">
            <button
              type="button"
              className={chartOpen ? "active" : ""}
              onClick={() => setChartOpen((open) => !open)}
              title="Visualize this table as a chart"
            >
              <BarChartToggleIcon size={14} />
              Chart
            </button>
            <button type="button" onClick={() => downloadCsv(exportRows, fileName)} title="Download table as CSV">
              <Download size={14} />
              CSV
            </button>
            <button type="button" onClick={() => downloadExcel(exportRows, fileName)} title="Download table as Excel">
              <Download size={14} />
              Excel
            </button>
          </div>
        </div>
      </div>
      {chartOpen && <ChartPanel headers={visibleHeaders} dataRows={dataRows} />}
      <div className="table-scroll">
        <table className="result-table">
          <thead>
            <tr>
              {visibleHeaders.map((header) => {
                const isSorted = sortState?.index === header.index;
                const isFilterOpen = openFilterIndex === header.index;
                return (
                  <th
                    key={`${header.label}-${header.index}`}
                    className={dragIndex === header.index ? "column-dragging" : ""}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const sourceIndex = Number(event.dataTransfer.getData("text/plain"));
                      handleColumnDrop(sourceIndex, header.index);
                    }}
                  >
                    <div className="table-header-cell">
                      <span
                        className="column-drag-handle"
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData("text/plain", String(header.index));
                          event.dataTransfer.effectAllowed = "move";
                          setDragIndex(header.index);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                        title="Drag to reorder column"
                      >
                        ⠿
                      </span>
                      <button
                        type="button"
                        className="table-sort-button"
                        onClick={() => handleSortClick(header.index)}
                        title="Sort by this column"
                      >
                        {formatHeaderLabel(header.label)}
                        <span className={`sort-arrow${isSorted ? " active" : ""}`}>
                          {isSorted ? (sortState.direction === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`table-filter-toggle${columnFilters[header.index] ? " active" : ""}`}
                        onClick={(event) => {
                          if (openFilterIndex === header.index) {
                            setOpenFilterIndex(null);
                            setFilterMenuPosition(null);
                            return;
                          }
                          setFilterMenuPosition(
                            clampColumnFilterMenuPosition(event.currentTarget.getBoundingClientRect())
                          );
                          setOpenFilterIndex(header.index);
                        }}
                        title="Filter this column"
                      >
                        ▾
                      </button>
                    </div>
                    {isFilterOpen && filterMenuPosition && (
                      <ColumnFilterMenu
                        header={header}
                        allDataRows={allDataRows}
                        selectedValues={columnFilters[header.index] || null}
                        position={filterMenuPosition}
                        onChange={(nextSet, distinctValues) => handleFilterChange(header.index, nextSet, distinctValues)}
                        onClear={() => handleFilterClear(header.index)}
                        onClose={() => {
                          setOpenFilterIndex(null);
                          setFilterMenuPosition(null);
                        }}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={isClosingBalanceRow(row, visibleHeaders) ? "table-row-highlight" : undefined}
              >
                {visibleHeaders.map((header) => (
                  <td key={`${rowIndex}-${header.index}`}>
                    <LinkedTableCell
                      cell={row[header.index]}
                      row={row}
                      header={header}
                      headerMap={headerMap}
                    />
                  </td>
                ))}
              </tr>
            ))}
            {dataRows.length === 0 && (
              <tr>
                <td className="table-empty-row" colSpan={visibleHeaders.length}>
                  No rows match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MarkdownLite({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let table = [];

  const flushTable = () => {
    if (!table.length) return;
    const rows = table
      .filter((line) => {
        const cells = line.split("|").slice(1, -1).map((cell) => cell.trim());
        return !cells.every((cell) => /^:?-{3,}:?$/.test(cell));
      })
      .map((line) => line.split("|").slice(1, -1).map((cell) => cell.trim()));

    blocks.push(<ResultTable rows={rows} tableIndex={blocks.length} key={`table-${blocks.length}`} />);
    table = [];
  };

  lines.forEach((line, index) => {
    if (line.trim().startsWith("|")) {
      table.push(line);
      return;
    }

    flushTable();
    if (!line.trim()) {
      blocks.push(<div className="message-gap" key={`gap-${index}`} />);
      return;
    }

    blocks.push(<p key={index}>{line}</p>);
  });
  flushTable();

  return blocks;
}

function formatTileValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (typeof value === "object") {
    const amount = value.amount ?? value.value;
    const symbol = value.symbol ?? value.currency ?? "";
    if (typeof amount === "number") {
      return `${symbol}${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`.trim();
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function MrSummaryTiles({ cards }) {
  if (!cards?.length) return null;

  return (
    <div className="mr-summary-cards">
      {cards.map((card, cardIndex) => (
        <div key={cardIndex} className="mr-summary-card">
          <div className="mr-summary-card-title">{card.title}</div>
          {card.rows?.length ? (
            <div className="mr-summary-tiles">
              {card.rows.map((row, rowIndex) => {
                const durationText = formatTileValue(row.duration);
                const balanceText = formatTileValue(row.balance);
                const isEmptyBalance = balanceText === "—";
                const isNetBalance = String(row.duration ?? "")
                  .toLowerCase()
                  .includes("net balance");
                return (
                  <div
                    key={rowIndex}
                    className={`mr-summary-tile${isNetBalance ? " mr-summary-tile--net" : ""}${isEmptyBalance ? " mr-summary-tile--empty" : ""}`}
                  >
                    <div className="mr-summary-tile-duration">{durationText}</div>
                    <div className="mr-summary-tile-balance">{balanceText}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mr-summary-empty">No rows.</div>
          )}
        </div>
      ))}
    </div>
  );
}

function MrOptionPicker({ messageId, options, selectedValue, disabled, multiSelect, onChoose, onSubmit }) {
  if (!options?.length) return null;

  const selectedValues = multiSelect ? (Array.isArray(selectedValue) ? selectedValue : []) : null;

  function toggleMulti(option) {
    let next;
    if (option.exclusive) {
      // The "All X" entry clears the filter — picking it deselects everything else.
      next = selectedValues.includes(option.value) ? [] : [option.value];
    } else {
      const withoutExclusive = selectedValues.filter((value) => {
        const matched = options.find((item) => item.value === value);
        return !matched?.exclusive;
      });
      next = withoutExclusive.includes(option.value)
        ? withoutExclusive.filter((value) => value !== option.value)
        : [...withoutExclusive, option.value];
    }
    onChoose(messageId, next);
  }

  const canSubmit = multiSelect ? selectedValues.length > 0 : Boolean(selectedValue);

  return (
    <form
      className="mr-options"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(messageId, options);
      }}
    >
      {options.map((option) => (
        <label key={option.value} className="mr-option">
          <input
            type={multiSelect ? "checkbox" : "radio"}
            name={`mr-option-${messageId}`}
            value={option.value}
            checked={multiSelect ? selectedValues.includes(option.value) : selectedValue === option.value}
            disabled={disabled}
            onChange={() => (multiSelect ? toggleMulti(option) : onChoose(messageId, option.value))}
          />
          {option.label}
        </label>
      ))}
      <button
        type="submit"
        className="mr-option-submit"
        disabled={disabled || !canSubmit}
      >
        Submit
      </button>
    </form>
  );
}

const CASHFLOW_FILTER_CATEGORIES = [
  { key: "transaction_type", label: "Transaction Type", fieldLabel: "Transaction type", choices: "transaction_type_choices", selected: "transaction_type_name", optionLabel: "name", multiple: false },
  { key: "lessor", label: "Lessor", fieldLabel: "Lessor", choices: "lessor_choices", selected: "lessor_names", optionLabel: "label", multiple: true },
  { key: "lessee", label: "Lessee", fieldLabel: "Lessee", choices: "lessee_choices", selected: "lessee_names", optionLabel: "label", multiple: true },
  { key: "asset", label: "Asset", fieldLabel: "Asset", choices: "asset_choices", selected: "asset_names", optionLabel: "label", multiple: true },
  { key: "lease_status", label: "Lease Status", fieldLabel: "Lease status", choices: "lease_status_choices", selected: "lease_status_labels", optionLabel: "label", multiple: true },
];

/**
 * Opt-in local UI preview for cashflow filters / MR tiles without backend data.
 *
 *   localStorage.setItem("AI_UI_PREVIEW", "cashflow,tiles")
 *   localStorage.setItem("AI_UI_PREVIEW", "all")
 *   localStorage.removeItem("AI_UI_PREVIEW")
 *
 * Refresh the page after changing the flag.
 */
function getUiPreviewModes() {
  try {
    const raw = localStorage.getItem("AI_UI_PREVIEW");
    if (!raw) return { cashflow: false, tiles: false };
    const value = String(raw).trim().toLowerCase();
    if (value === "1" || value === "true" || value === "all") {
      return { cashflow: true, tiles: true };
    }
    const parts = value.split(/[,\s]+/).filter(Boolean);
    return {
      cashflow: parts.includes("cashflow") || parts.includes("filters"),
      tiles: parts.includes("tiles") || parts.includes("mr")
    };
  } catch {
    return { cashflow: false, tiles: false };
  }
}

const UI_PREVIEW_TILE_CARDS = [
  {
    title: "MSN 61735",
    rows: [
      { duration: "C1 CHECK", balance: 1263445.06 },
      { duration: "LG GROUP", balance: 1046126.09 },
      { duration: "ADDITIONAL TAX", balance: null },
      { duration: "PENALTY", balance: null },
      { duration: "NET BALANCE", balance: 2309571.15 }
    ]
  },
  {
    title: "ESN - 901280",
    rows: [
      { duration: "ENGINE SUPPLEMENT", balance: 1126347.97 },
      { duration: "ENGINE LLP RESTORATION", balance: 295863.56 },
      { duration: "ADDITIONAL TAX", balance: null },
      { duration: "PENALTY", balance: null },
      { duration: "NET BALANCE", balance: 1422211.53 }
    ]
  }
];

const UI_PREVIEW_CASHFLOW_REPORT = {
  filterBar: true,
  transaction_type_choices: [
    { id: 1, name: "Rent" },
    { id: 2, name: "MR Accrual" },
    { id: 3, name: "Supplemental Rent" }
  ],
  lessor_choices: [
    { id: 1, label: "Titan Aviation Leasing" },
    { id: 2, label: "Acumen Lessor" }
  ],
  lessee_choices: [
    { id: 1, label: "Abu Dhabi Amiri Flight" },
    { id: 2, label: "Demo Lessee" }
  ],
  asset_choices: [
    { id: 1, key: "msn61735", label: "MSN 61735" },
    { id: 2, key: "esn901280", label: "ESN - 901280" }
  ],
  lease_status_choices: [
    { id: 1, key: "active", label: "Active" },
    { id: 2, key: "closed", label: "Closed" }
  ],
  transaction_type_name: null,
  lessor_names: [],
  lessee_names: [],
  asset_names: [],
  lease_status_labels: []
};

function UiPreviewPanel({ modes, onCashflowSubmit }) {
  if (!modes.cashflow && !modes.tiles) return null;

  return (
    <article className="message-row assistant">
      <div className="message-meta">
        <span className="assistant-label">
          <img src={aiChatIcon} alt="" />
          AI Chat Assistant
        </span>
      </div>
      <div className="message-bubble">
        {modes.tiles && (
          <>
            <p>
              MR report summary for lease <strong>TIT-LSE-2017-0002</strong> — report type{" "}
              <strong>Balance</strong>.
            </p>
            <MrSummaryTiles cards={UI_PREVIEW_TILE_CARDS} />
          </>
        )}
        {modes.cashflow && (
          <>
            <p>Please choose the filters you&apos;d like to apply, then submit.</p>
            <CashflowFilterBar
              report={UI_PREVIEW_CASHFLOW_REPORT}
              disabled={false}
              onSubmit={onCashflowSubmit}
            />
          </>
        )}
      </div>
    </article>
  );
}

function CashflowFilterBar({ report, disabled, onSubmit }) {
  const [selections, setSelections] = useState(() => Object.fromEntries(
    CASHFLOW_FILTER_CATEGORIES.map((category) => {
      const current = report[category.selected];
      const names = Array.isArray(current) ? current : current ? [current] : [];
      const options = (report[category.choices] || []).filter((option) => option.id != null || option.key != null);
      const selected = options.filter((option) => names.some((name) =>
        String(name).toLocaleLowerCase() === String(option[category.optionLabel]).toLocaleLowerCase()
      ));
      return [category.key, category.multiple ? selected : selected[0] || null];
    })
  ));

  function submit(event) {
    event.preventDefault();
    const reply = CASHFLOW_FILTER_CATEGORIES.map((category) => {
      const value = selections[category.key];
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      return `${category.label}: ${selected.length ? selected.map((option) => option[category.optionLabel]).join(", ") : "All"}`;
    }).join("; ");
    onSubmit(reply);
  }

  return (
    <form className="cashflow-filter-bar" onSubmit={submit}>
      <div className="cashflow-filter-heading">Advanced Filters</div>
      <div className="cashflow-filter-fields">
        {CASHFLOW_FILTER_CATEGORIES.map((category) => {
          const options = (report[category.choices] || []).filter((option) => option.id != null || option.key != null);
          return (
            <Autocomplete
              key={category.key}
              className="cashflow-filter-field"
              size="small"
              multiple={category.multiple}
              limitTags={1}
              options={options}
              value={selections[category.key]}
              getOptionLabel={(option) => String(option[category.optionLabel] || "")}
              isOptionEqualToValue={(option, value) => option.id === value.id && option.key === value.key && option.asset_type === value.asset_type}
              onChange={(_, value) => setSelections((current) => ({ ...current, [category.key]: value }))}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={category.fieldLabel}
                  placeholder={category.multiple && selections[category.key]?.length ? "" : "All"}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
              )}
            />
          );
        })}
      </div>
      <div className="cashflow-filter-actions">
        <button type="submit" className="mr-option-submit cashflow-filter-submit" disabled={disabled}>
          Apply filters
        </button>
      </div>
    </form>
  );
}

function FewShotReferenceList({ questions }) {
  if (!questions?.length) return null;

  return (
    <div className="fewshot-panel">
      <div className="sql-title">
        <Search size={14} />
        Few-shot references used
      </div>
      <ul>
        {questions.map((item, index) => (
          <li key={`${item}-${index}`}>{capitalizeQuestion(item)}</li>
        ))}
      </ul>
    </div>
  );
}

function resolveThreadTitle(thread) {
  const firstUserMessage = thread?.messages?.find(
    (message) => message.role === "user"
  );
  const rawTitle =
    firstUserMessage?.content?.trim().replace(/\s+/g, " ") ||
    thread?.title ||
    "New chat";
  return capitalizeQuestion(rawTitle);
}

export function App() {
  const navigate = useNavigate();
  const [unauthorized, setUnauthorized] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [threadPagination, setThreadPagination] = useState({
    page: 1,
    total_pages: 1,
    count: 0
  });
  const [threadDetailLoading, setThreadDetailLoading] = useState(false);
  const [threadListLoading, setThreadListLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [questionSuggestions, setQuestionSuggestions] = useState([]);
  const [questionSuggestionsLoading, setQuestionSuggestionsLoading] = useState(false);
  const [activeQuestionSuggestion, setActiveQuestionSuggestion] = useState(-1);
  const [questionInputFocused, setQuestionInputFocused] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [overallTokenSummary, setOverallTokenSummary] = useState({
    input: 0,
    output: 0,
    total: 0,
    retries: 0,
    answers: 0
  });
  const [quotaMessage, setQuotaMessage] = useState("");
  const [requestError, setRequestError] = useState(null);
  const [rechargeRequests, setRechargeRequests] = useState([]);
  const [rechargePackages, setRechargePackages] = useState([]);
  const [rechargePagination, setRechargePagination] = useState({
    page: 1,
    total_pages: 1,
    count: 0,
    page_size: RECHARGE_DEFAULT_PAGE_SIZE
  });
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [rechargeCancellingId, setRechargeCancellingId] = useState(null);
  const [rechargeConfirmation, setRechargeConfirmation] = useState(null);
  const [rechargeError, setRechargeError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showSql, setShowSql] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(true);
  const [sendingWithMemory, setSendingWithMemory] = useState(false);
  const [feedbackBusyId, setFeedbackBusyId] = useState(null);
  const [feedbackCommentTarget, setFeedbackCommentTarget] = useState(null);
  const [mrOptionSelections, setMrOptionSelections] = useState({});
  const endRef = useRef(null);
  const { accepted: consentAccepted, policy: consentPolicy, requestReconsent } = useAiConsent();
  const { permission } = useSelector((state) => state.leases);
  const lastThreadSearchRef = useRef("");
  const selectedQuestionSuggestionRef = useRef("");
  const questionInputRef = useRef(null);
  const canManageTokens = hasTokenAdminAccess(permission);
  const tokenExhaustedMessage = canManageTokens
  ? "Not enough token, please select recharge tokens."
  : "Not enough token, please contact your admin to recharge.";
  const uiPreviewModes = getUiPreviewModes();

  useEffect(() => {
    const handleUnauthorized = () => setUnauthorized(true);
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  useEffect(() => {
    trackActivity("Page Viewed", { page_title: "AI Chat Assistant" });
  }, []);

  useEffect(() => {
    const searchTerm = question.trim();
    if (
      searchTerm.length < 2 ||
      sending ||
      searchTerm === selectedQuestionSuggestionRef.current
    ) {
      setQuestionSuggestions([]);
      setQuestionSuggestionsLoading(false);
      setActiveQuestionSuggestion(-1);
      return undefined;
    }

    selectedQuestionSuggestionRef.current = "";
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setQuestionSuggestionsLoading(true);
      try {
        const response = await MockApi.loadFewShots({
          search: searchTerm,
          limit: 8
        });
        if (cancelled) return;

        const seen = new Set();
        const suggestions = (response?.items || [])
          .map((item) => String(item.Question || item.question || "").trim())
          .filter((item) => {
            const normalized = item.toLowerCase();
            if (!item || seen.has(normalized)) return false;
            seen.add(normalized);
            return true;
          })
          .slice(0, 8);

        setQuestionSuggestions(suggestions);
        setActiveQuestionSuggestion(-1);
      } catch {
        if (!cancelled) {
          setQuestionSuggestions([]);
          setActiveQuestionSuggestion(-1);
        }
      } finally {
        if (!cancelled) setQuestionSuggestionsLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [question, sending]);

  useEffect(() => {
    if (!consentAccepted) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      try {
        const [threadsResult, usageResult, overallResult] = await Promise.allSettled([
          MockApi.loadThreads({ page: 1, pageSize: 20 }),
          MockApi.loadTokenUsage(),
          MockApi.loadOverallTokens()
        ]);

        if (cancelled) return;

        const blockedResult = [threadsResult, usageResult, overallResult].find(
          (result) =>
            result.status === "rejected" && isConsentCheckCandidate(result.reason)
        );
        if (blockedResult) {
          const opened = await requestReconsent(blockedResult.reason);
          if (cancelled) return;
          if (opened) {
            setQuotaMessage("");
            return;
          }
        }

        if (threadsResult.status === "fulfilled") {
          const threadPage = threadsResult.value;
          setThreads(threadPage.results || []);
          setThreadPagination({
            page: threadPage.page || 1,
            total_pages: threadPage.total_pages || 1,
            count: threadPage.count || 0
          });
          setActiveThreadId(threadPage.results?.[0]?.id || null);
        } else {
          setThreads([]);
          setActiveThreadId(null);
          setQuotaMessage(
            threadsResult.reason?.message || "Unable to load chat sessions."
          );
        }

        if (usageResult.status === "fulfilled") {
          setTokenUsage(usageResult.value);
        }

        if (overallResult.status === "fulfilled") {
          setOverallTokenSummary(overallResult.value);
        }
      } catch (error) {
        if (!cancelled) {
          if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
            setQuotaMessage("");
          } else {
            setQuotaMessage(error.message || "Unable to load chat sessions.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [consentAccepted, requestReconsent]);

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedThread() {
      if (!consentAccepted || !activeThreadId) {
        if (!activeThreadId) setActiveThread(null);
        return;
      }
      setThreadDetailLoading(true);
      try {
        const thread = await MockApi.loadThread(activeThreadId);
        if (!cancelled) {
          const title = resolveThreadTitle(thread);
          setActiveThread({ ...thread, title });
          setThreads((current) =>
            current.map((item) =>
              item.id === thread.id ? { ...item, title } : item
            )
          );
        }
      } catch (error) {
        if (!cancelled) {
          if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
            setQuotaMessage("");
          } else {
            setActiveThread(null);
            setQuotaMessage(error.message || "Unable to load this thread.");
          }
        }
      } finally {
        if (!cancelled) setThreadDetailLoading(false);
      }
    }

    loadSelectedThread();
    return () => {
      cancelled = true;
    };
  }, [activeThreadId, consentAccepted]);

  useEffect(() => {
    if (!consentAccepted || loading) return undefined;

    const searchTerm = search.trim();
    if (searchTerm === lastThreadSearchRef.current) return undefined;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setThreadListLoading(true);
      try {
        const threadPage = await MockApi.loadThreads({
          page: 1,
          pageSize: 20,
          search: searchTerm
        });
        if (cancelled) return;
        setThreads(threadPage.results || []);
        setThreadPagination({
          page: threadPage.page || 1,
          total_pages: threadPage.total_pages || 1,
          count: threadPage.count || 0
        });
        lastThreadSearchRef.current = searchTerm;
      } catch (error) {
        if (!cancelled) {
          if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
            setQuotaMessage("");
          } else {
            setQuotaMessage(error.message || "Unable to search chat sessions.");
          }
        }
      } finally {
        if (!cancelled) setThreadListLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [search, loading, consentAccepted]);

  const activeTokenSummary = useMemo(() => {
    const messages = activeThread?.messages || [];
    return messages.reduce(
      (summary, message) => {
        const tokens = normalizeTokens(message.tokens);
        if (!tokens) return summary;
        return {
          input: summary.input + tokens.input,
          output: summary.output + tokens.output,
          total: summary.total + tokens.total,
          retries: summary.retries + tokens.retries,
          answers: summary.answers + 1
        };
      },
      { input: 0, output: 0, total: 0, retries: 0, answers: 0 }
    );
  }, [activeThread]);

  const activeThreadSummary = useMemo(
    () => threads.find((thread) => thread.id === activeThreadId),
    [threads, activeThreadId]
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeThread?.messages.length, sending]);

  function applyThreadUpdate(thread, { isNew = false } = {}) {
    const title = resolveThreadTitle(thread);
    const summary = {
      id: thread.id,
      title,
      lessor_id: thread.lessor_id,
      user_id: thread.user_id,
      createdAt: thread.createdAt,
      updatedAt: thread.updatedAt,
      questionCount:
        thread.messages?.filter((message) => message.role === "user").length || 0
    };
    setActiveThread({ ...thread, title });
    setActiveThreadId(thread.id);
    setThreads((current) => [
      summary,
      ...current.filter((item) => item.id !== thread.id)
    ]);
    if (isNew) {
      setThreadPagination((current) => {
        const count = current.count + 1;
        return {
          ...current,
          count,
          total_pages: Math.max(1, Math.ceil(count / 20))
        };
      });
    }
  }

  function patchMessageFeedback(messageId, { rating, comments }) {
    setActiveThread((current) => {
      if (!current?.messages?.length) return current;
      return {
        ...current,
        messages: current.messages.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedback: rating || getFeedbackRating(message),
                comments: comments ?? getFeedbackComments(message)
              }
            : message
        )
      };
    });
  }

  async function refreshTokenUsage() {
    try {
      const [usageResult, overallResult] = await Promise.allSettled([
        MockApi.loadTokenUsage(),
        MockApi.loadOverallTokens()
      ]);
      if (usageResult.status === "fulfilled") setTokenUsage(usageResult.value);
      if (overallResult.status === "fulfilled") {
        setOverallTokenSummary(overallResult.value);
      }
    } catch {
      // Token refresh should not interrupt chat flow.
    }
  }

  async function handleLoadMoreThreads() {
    if (threadListLoading || threadPagination.page >= threadPagination.total_pages) return;
    setThreadListLoading(true);
    try {
      const nextPage = await MockApi.loadThreads({
        page: threadPagination.page + 1,
        pageSize: 20,
        search: search.trim()
      });
      setThreads((current) => {
        const existingIds = new Set(current.map((thread) => thread.id));
        return [
          ...current,
          ...(nextPage.results || []).filter((thread) => !existingIds.has(thread.id))
        ];
      });
      setThreadPagination({
        page: nextPage.page || threadPagination.page + 1,
        total_pages: nextPage.total_pages || 1,
        count: nextPage.count || 0
      });
    } catch (error) {
      setQuotaMessage(error.message || "Unable to load more chat sessions.");
    } finally {
      setThreadListLoading(false);
    }
  }

  function handleSelectThread(threadId) {
    if (threadId === activeThreadId) return;
    setActiveThread(null);
    setQuotaMessage("");
    setRequestError(null);
    setActiveThreadId(threadId);
  }

  async function handleNewThread() {
    trackActivity("Item Clicked", {
      page_title: "AI Chat Assistant",
      page_type: "New Chat"
    });
    setQuotaMessage("");
    setRequestError(null);
    try {
      const thread = await MockApi.createThread();
      applyThreadUpdate(thread, { isNew: true });
    } catch (error) {
      if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
        setQuotaMessage("");
        setSending(false);
        setSendingWithMemory(false);
        return;
      }
      setQuotaMessage(error.message || "Unable to create a new chat.");
    }
  }

  async function loadRechargeHistory(
    page = 1,
    pageSize = rechargePagination.page_size || RECHARGE_DEFAULT_PAGE_SIZE
  ) {
    setRechargeLoading(true);
    try {
      const history = await MockApi.loadTokenRechargeRequests({ page, pageSize });
      const paginationInfo = normalizeRechargePagination(history, page, pageSize);
      setRechargeRequests(history.results || history.list || []);
      setRechargePagination(paginationInfo);
    } catch (error) {
      setRechargeError(error.message || "Unable to load recharge history.");
    } finally {
      setRechargeLoading(false);
    }
  }

  async function handleOpenRecharge() {
    if (!canManageTokens) return;
    trackActivity("Item Clicked", {
      page_title: "AI Chat Assistant",
      page_type: "Recharge Tokens"
    });
    setRechargeConfirmation(null);
    setRechargeError("");
    setRechargeOpen(true);
    setRechargeLoading(true);
    try {
      const [packages, history] = await Promise.all([
        MockApi.loadTokenRechargePackages(),
        MockApi.loadTokenRechargeRequests({
          page: 1,
          pageSize: RECHARGE_DEFAULT_PAGE_SIZE
        })
      ]);
      const paginationInfo = normalizeRechargePagination(
        history,
        1,
        RECHARGE_DEFAULT_PAGE_SIZE
      );
      setRechargePackages(packages);
      setRechargeRequests(history.results || history.list || []);
      setRechargePagination(paginationInfo);
    } catch (error) {
      setRechargeError(error.message || "Unable to load recharge details.");
    } finally {
      setRechargeLoading(false);
    }
  }

  async function handleRechargeConfirm(tokenAmount, comments) {
    const trimmedComments = String(comments || "").trim();
    if (!trimmedComments) {
      setRechargeError("Comments are required to submit a token request.");
      return;
    }

    setRechargeSubmitting(true);
    setRechargeError("");
    try {
      const response = await MockApi.createTokenRechargeRequest(
        tokenAmount,
        trimmedComments
      );
      trackActivity("Item Added", {
        page_title: "AI Chat Assistant",
        success_msg: "Token recharge request submitted",
        payload: { token_amount: tokenAmount }
      });
      setRechargeConfirmation(response.request);
      await loadRechargeHistory(
        1,
        rechargePagination.page_size || RECHARGE_DEFAULT_PAGE_SIZE
      );
    } catch (error) {
      trackActivity("Item Added", {
        page_title: "AI Chat Assistant",
        error_msg: error.message || "Unable to submit the recharge request.",
        payload: { token_amount: tokenAmount }
      });
      setRechargeError(error.message || "Unable to submit the recharge request.");
    } finally {
      setRechargeSubmitting(false);
    }
  }

  async function handleCancelRechargeRequest(requestId, comments) {
    setRechargeCancellingId(requestId);
    try {
      const response = await MockApi.cancelTokenRechargeRequest(
        requestId,
        comments
      );
      await loadRechargeHistory(
        rechargePagination.page,
        rechargePagination.page_size || RECHARGE_DEFAULT_PAGE_SIZE
      );
      return response;
    } finally {
      setRechargeCancellingId(null);
    }
  }

  async function submitQuestionText(rawText, { restoreOnError = false } = {}) {
    const trimmed = (rawText || "").trim();
    if (!trimmed || sending) return;

    setSending(true);
    setQuotaMessage("");
    setRequestError(null);

    try {
      let threadId = activeThreadId;
      let isFirstQuestion = false;
      if (!threadId) {
        const thread = await MockApi.createThread(trimmed);
        threadId = thread.id;
        applyThreadUpdate(thread, { isNew: true });
        isFirstQuestion = true;
      } else {
        // Prefer loaded messages; fall back to sidebar summary while detail is still loading.
        const existingQuestions = activeThread
          ? activeThread.messages.filter((message) => message.role === "user")
              .length
          : activeThreadSummary?.questionCount || 0;
        isFirstQuestion = existingQuestions === 0;
      }

      // First question has no prior context; enable memory from the second question on.
      const effectiveMemoryEnabled = isFirstQuestion ? false : memoryEnabled;
      setSendingWithMemory(effectiveMemoryEnabled);

      const { thread, token_usage: nextTokenUsage } = await MockApi.askQuestion({
        threadId,
        question: trimmed,
        memoryEnabled: effectiveMemoryEnabled
      });
      trackActivity("Item Added", {
        page_title: "AI Chat Assistant",
        success_msg: "Question submitted",
        item_id: threadId
      });
      if (nextTokenUsage) setTokenUsage(nextTokenUsage);
      applyThreadUpdate(thread);
      await refreshTokenUsage();
    } catch (error) {
      trackActivity("Item Added", {
        page_title: "AI Chat Assistant",
        error_msg: error.message || "Unable to send the question.",
        item_id: activeThreadId
      });
      if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
        if (restoreOnError) setQuestion(trimmed);
        setQuotaMessage("");
        setSending(false);
        setSendingWithMemory(false);
      } else if (error.status === 402) {
        if (restoreOnError) setQuestion(trimmed);
        setTokenUsage(error.data?.token_usage || tokenUsage);
        setQuotaMessage(
          error.data?.message || tokenExhaustedMessage
        );
      } else {
        setRequestError({ question: trimmed, message: formatQuestionError(error) });
      }
    } finally {
      setSending(false);
      setSendingWithMemory(false);
    }
  }

  async function handleSubmit(event) {
    event?.preventDefault();
    const trimmed = capitalizeQuestion(question.trim());
    if (!trimmed || sending) return;

    setQuestion("");
    selectedQuestionSuggestionRef.current = "";
    setQuestionSuggestions([]);
    setActiveQuestionSuggestion(-1);

    await submitQuestionText(trimmed, { restoreOnError: true });
  }

  function handleMrOptionChoose(messageId, value) {
    setMrOptionSelections((current) => ({ ...current, [messageId]: value }));
  }

  async function handleMrOptionSubmit(messageId, options) {
    const value = mrOptionSelections[messageId];
    const isMulti = Array.isArray(value);
    if (!value || (isMulti && value.length === 0)) return;
    setMrOptionSelections((current) => {
      const next = { ...current };
      delete next[messageId];
      return next;
    });
    // Numbered picks (leases, assemblies, events, report types, filter
    // choices) submit "<number>. <label>" so the reply reads clearly in chat
    // history. System action keys (the switch menu, "skip") aren't numbers —
    // their raw value (e.g. "cashflow_switch_year") is an implementation
    // detail the user shouldn't see, so submit just the label; the backend
    // recognizes it by that label text.
    if (isMulti) {
      const selectedOptions = value
        .map((item) => options.find((option) => option.value === item))
        .filter(Boolean);
      if (!selectedOptions.length) return;
      const text = selectedOptions
        .map((option) =>
          /^\d+$/.test(option.value) ? `${option.value}. ${option.label}` : option.label
        )
        .join(", ");
      await submitQuestionText(text);
      return;
    }
    const option = options.find((item) => item.value === value);
    if (!option) {
      await submitQuestionText(value);
      return;
    }
    const isNumbered = /^\d+$/.test(option.value);
    await submitQuestionText(isNumbered ? `${option.value}. ${option.label}` : option.label);
  }

  async function handleRegenerate(message) {
    if (!activeThreadId || !activeThread || sending) return;
    trackActivity("Item Clicked", {
      page_title: "AI Chat Assistant",
      page_type: "Regenerate"
    });

    const messageIndex = activeThread.messages.findIndex(
      (item) => item.id === message.id
    );
    const originalQuestion =
      activeThread.messages.find(
        (item) =>
          item.role === "user" &&
          item.questionId &&
          item.questionId === message.questionId
      ) ||
      [...activeThread.messages.slice(0, messageIndex)]
        .reverse()
        .find((item) => item.role === "user");

    if (!originalQuestion?.content) {
      setQuotaMessage("Unable to find the original question for this answer.");
      return;
    }

    setSending(true);
    setRegeneratingMessageId(message.id);
    setQuotaMessage("");
    try {
      const existingQuestions =
        activeThread.messages.filter((item) => item.role === "user").length;
      const effectiveMemoryEnabled =
        existingQuestions <= 1 ? false : memoryEnabled;
      setSendingWithMemory(effectiveMemoryEnabled);

      const { thread, token_usage: nextTokenUsage } = await MockApi.askQuestion({
        threadId: activeThreadId,
        question: capitalizeQuestion(originalQuestion.content),
        memoryEnabled: effectiveMemoryEnabled
      });
      if (nextTokenUsage) setTokenUsage(nextTokenUsage);
      applyThreadUpdate(thread);
      await refreshTokenUsage();
    } catch (error) {
      if (isConsentCheckCandidate(error) && (await requestReconsent(error))) {
        setQuotaMessage("");
        setSending(false);
        setSendingWithMemory(false);
      } else if (error.status === 402) {
        setTokenUsage(error.data?.token_usage || tokenUsage);
        setQuotaMessage(
          error.data?.message || tokenExhaustedMessage
        );
      } else {
        setQuotaMessage(error.message || "Unable to regenerate the answer.");
      }
    } finally {
      setSending(false);
      setSendingWithMemory(false);
      setRegeneratingMessageId(null);
    }
  }

  async function handleFeedback(message, rating) {
    if (!activeThreadId || feedbackBusyId) return;
    trackActivity("Item Clicked", {
      page_title: "AI Chat Assistant",
      page_type: rating === "positive" ? "Useful" : "Not Useful"
    });
    if (rating === "positive" && getFeedbackRating(message) === "positive") return;

    if (rating === "negative") {
      const latest =
        activeThread?.messages?.find((item) => item.id === message.id) || message;
      setFeedbackCommentTarget(latest);
      return;
    }

    setFeedbackBusyId(message.id);
    try {
      const response = await MockApi.submitFeedback({
        threadId: activeThreadId,
        messageId: message.id,
        rating
      });
      if (response?.thread) applyThreadUpdate(response.thread);
      else patchMessageFeedback(message.id, { rating: "positive" });
    } catch (error) {
      setQuotaMessage(error.message || "Unable to submit feedback.");
    } finally {
      setFeedbackBusyId(null);
    }
  }

  async function handleNegativeFeedbackSubmit(comments) {
    if (!activeThreadId || !feedbackCommentTarget || feedbackBusyId) return;
    const message = feedbackCommentTarget;
    const trimmedComments = String(comments || "").trim();

    if (
      getFeedbackRating(message) === "negative" &&
      getFeedbackComments(message) === trimmedComments
    ) {
      setFeedbackCommentTarget(null);
      return;
    }

    setFeedbackBusyId(message.id);
    try {
      const response = await MockApi.submitFeedback({
        threadId: activeThreadId,
        messageId: message.id,
        rating: "negative",
        comments: trimmedComments
      });
      const savedComments =
        trimmedComments || response?.feedback?.comments || "";
      if (response?.thread) {
        applyThreadUpdate(
          mergeFeedbackIntoThread(response.thread, message.id, {
            rating: "negative",
            comments: savedComments
          })
        );
      } else {
        patchMessageFeedback(message.id, {
          rating: "negative",
          comments: savedComments
        });
      }
      setFeedbackCommentTarget(null);
    } catch (error) {
      setQuotaMessage(error.message || "Unable to submit feedback.");
    } finally {
      setFeedbackBusyId(null);
    }
  }

  function selectQuestionSuggestion(suggestion) {
    const nextQuestion = capitalizeQuestion(suggestion);
    selectedQuestionSuggestionRef.current = nextQuestion;
    setQuestion(nextQuestion);
    setQuestionSuggestions([]);
    setActiveQuestionSuggestion(-1);
    window.requestAnimationFrame(() => questionInputRef.current?.focus());
  }

  function handleExample(value) {
    selectQuestionSuggestion(value);
  }

  if (loading && consentAccepted) {
    return (
      <div className="ai-chat-app">
        <main className="loading-screen">
          <Loader2 className="spin" size={28} />
          <span>Loading chat sessions</span>
        </main>
        {unauthorized && <UnauthorizedOverlay />}
      </div>
    );
  }

  return (
    <div className="ai-chat-app">
      <div className={`app-shell ${sidebarOpen ? "" : "sidebar-collapsed"}`}>
      <aside className="sidebar">
        <div className="topbar-heading-wrap">
          <AiChatBrand />
        </div>
       <div className="sidebar-header">
          {/* <button className="icon-button" onClick={() => setSidebarOpen(false)} title="Collapse sidebar">
            <PanelLeft size={18} />
          </button> */}
           <label className="search-box">
          <Search size={15} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search Question"
          />
        </label>
          <button className="new-thread" onClick={handleNewThread}>
            <MessageSquarePlus size={16} />
            New chat
          </button>
        </div>

        <div className="thread-list">
          {threads.map((thread) => (
            <button
              key={thread.id}
              className={`thread-item ${thread.id === activeThreadId ? "active" : ""}`}
              onClick={() => handleSelectThread(thread.id)}
            >
              <span title={capitalizeQuestion(thread.title || "New chat")}>
                {capitalizeQuestion(thread.title || "New chat")}
              </span>
              <small>
                {formatDate(thread.updatedAt)}
                {(thread.questionCount || 0) > 0 && (
                  <span className="thread-question-count">
                    {thread.questionCount}{" "}
                    {thread.questionCount === 1 ? "question" : "questions"}
                  </span>
                )}
              </small>
            </button>
          ))}
          {threadListLoading && !threads.length && (
            <div className="thread-list-status">
              <Loader2 size={12} />
              Searching questions
            </div>
          )}
          {!threadListLoading && search.trim() && !threads.length && (
            <div className="thread-list-status">No matching questions</div>
          )}
          {!threadListLoading && !search.trim() && !threads.length && (
            <div className="thread-list-status">No chat sessions yet</div>
          )}
          {threads.length < threadPagination.count &&
            threadPagination.page < threadPagination.total_pages && (
            <button
              type="button"
              className="show-more-threads"
              onClick={handleLoadMoreThreads}
              disabled={threadListLoading}
            >
              {threadListLoading ? (
                <>
                  <Loader2 size={12} />
                  Loading
                </>
              ) : (
                `Show more (${Math.max(threadPagination.count - threads.length, 0)} remaining)`
              )}
            </button>
          )}
        </div>

        <div className="save-panel">
          <UserTokenUsage
            usage={tokenUsage}
            onRecharge={handleOpenRecharge}
            canRecharge={canManageTokens}
          />
          <div className="token-title-row">
            <div className="token-title-meta">
              <div className="panel-title token-title">Tokens Consumed</div>
              <div className="save-count">
                {overallTokenSummary.answers
                  ? `${Number(overallTokenSummary.total).toLocaleString()} total across ${Number(
                      overallTokenSummary.answers
                    ).toLocaleString()} answers`
                  : "No token usage yet"}
              </div>
            </div>
            {canManageTokens ? (
              <div className="token-action-buttons">
                <button
                  type="button"
                  className="token-nav-button token-nav-button--primary"
                  title="Token Requests"
                  onClick={() => {
                    trackActivity("Item Clicked", {
                      page_title: "AI Chat Assistant",
                      page_type: "Token Requests"
                    });
                    navigate("/ai-token-management");
                  }}
                >
                  Token Requests
                </button>
                <button
                  type="button"
                  className="token-nav-button token-nav-button--outlined"
                  title="Usage Report"
                  onClick={() => {
                    trackActivity("Item Clicked", {
                      page_title: "AI Chat Assistant",
                      page_type: "Usage Report"
                    });
                    navigate("/ai-token-usage");
                  }}
                >
                  Usage Report
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </aside>

      <main className="chat-panel">
        <header className="topbar">
           <div>
          <h1
            title={capitalizeQuestion(
              activeThread?.title ||
                activeThreadSummary?.title ||
                "AI Chat Assistant"
            )}
          >
            {capitalizeQuestion(
              activeThread?.title ||
                activeThreadSummary?.title ||
                "AI Chat Assistant"
            )}
          </h1>
            <p>
              {activeThread
                ? `${activeThread.messages.filter((msg) => msg.role === "user").length} ${
                    activeThread.messages.filter((msg) => msg.role === "user").length === 1
                      ? "question"
                      : "questions"
                  } in this thread`
                : activeThreadSummary
                  ? `${activeThreadSummary.questionCount || 0} ${
                      (activeThreadSummary.questionCount || 0) === 1
                        ? "question"
                        : "questions"
                    } in this thread`
                : "Start a new lease data conversation"}
            </p>
            </div>
          <button
            className={`toggle-button ${showSql ? "active" : ""}`}
            type="button"
            style={{display:'none'}}
            onClick={() => setShowSql((value) => !value)}
            title="Show generated SQL"
          >
            <Code2 size={16} />
            Generated SQL
          </button>
        </header>

        <section className="messages">
          {threadDetailLoading && (
            <div className="thread-detail-loading">
              <Loader2 size={20} />
              <span>Loading messages</span>
            </div>
          )}

          {(uiPreviewModes.cashflow || uiPreviewModes.tiles) && (
            <UiPreviewPanel
              modes={uiPreviewModes}
              onCashflowSubmit={(reply) => {
                // Preview only — log the composed filter reply; do not call the API.
                // eslint-disable-next-line no-console
                console.log("[AI_UI_PREVIEW] cashflow filters:", reply);
              }}
            />
          )}

          {!threadDetailLoading && !activeThread?.messages.length && (
            <div className="empty-state">
              <div className="empty-state-heading">
                <h2>Ask about contracts, invoices, assets, or reports.</h2>
                <p className="examples-label">Sample questions</p>
              </div>
              <div className="examples">
                {exampleQuestions.map((item) => (
                  <button key={item} onClick={() => handleExample(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeThread?.messages.map((message) => (
            <article key={message.id} className={`message-row ${message.role}`}>
              <div className="message-meta">
                {message.role === "user" ? (
                  <span>You</span>
                ) : (
                  <span className="assistant-label">
                    <img src={aiChatIcon} alt="" />
                    AI Chat Assistant
                  </span>
                )}
                <time>
                  <Clock3 size={13} />
                  {formatDate(message.createdAt)}
                </time>
              </div>
              <div className="message-bubble">
                <MarkdownLite
                  text={
                    message.role === "user"
                      ? capitalizeQuestion(message.content)
                      : message.content
                  }
                />
                {message.role === "assistant" && message.externalReport?.cards?.length > 0 && (
                  <MrSummaryTiles cards={message.externalReport.cards} />
                )}
                {message.role === "assistant" &&
                  message.externalReport?.filterBar &&
                  message.id === activeThread.messages[activeThread.messages.length - 1]?.id && (
                    <CashflowFilterBar
                      report={message.externalReport}
                      disabled={sending}
                      onSubmit={submitQuestionText}
                    />
                  )}
                {message.role === "assistant" &&
                  message.externalReport?.options?.length > 0 &&
                  message.id ===
                    activeThread.messages[activeThread.messages.length - 1]?.id && (
                    <MrOptionPicker
                      messageId={message.id}
                      options={message.externalReport.options}
                      selectedValue={mrOptionSelections[message.id]}
                      disabled={sending}
                      multiSelect={Boolean(message.externalReport.multiSelect)}
                      onChoose={handleMrOptionChoose}
                      onSubmit={handleMrOptionSubmit}
                    />
                  )}
                {message.role === "assistant" && showSql && message.rawSql && (
                  <div className="sql-panel">
                    <div className="sql-title">
                      <Code2 size={14} />
                      Generated SQL
                    </div>
                    <pre><code>{message.rawSql}</code></pre>
                    <FewShotReferenceList questions={message.fewShotQuestions} />
                  </div>
                )}
                {message.role === "assistant" && (
                  <div className="message-footer-row">
                    <TokenSummary tokens={message.tokens} />
                    <div className="message-actions">
                      <button
                        type="button"
                        className="feedback-button regenerate-button"
                        onClick={() => handleRegenerate(message)}
                        disabled={
                          sending ||
                          normalizeTokenUsage(tokenUsage)?.available < 1
                        }
                        title="Ask this question again and create a new answer"
                      >
                        {regeneratingMessageId === message.id ? (
                          <Loader2 className="spin" size={12} />
                        ) : (
                          <Refresh size={13} />
                        )}
                        Regenerate
                      </button>
                      <button
                        type="button"
                        className={
                          getFeedbackRating(message) === "positive"
                            ? "feedback-button active"
                            : "feedback-button"
                        }
                        onClick={() => handleFeedback(message, "positive")}
                        disabled={feedbackBusyId === message.id || !message.rawSql}
                        title="Save this answer as a few-shot reference"
                      >
                        {feedbackBusyId === message.id ? (
                          <Loader2 className="spin" size={12} />
                        ) : (
                          <ThumbsUp className="thumb-icon" size={13} />
                        )}
                        Useful
                      </button>
                      <button
                        type="button"
                        className={
                          getFeedbackRating(message) === "negative"
                            ? "feedback-button negative active"
                            : "feedback-button negative"
                        }
                        onClick={() => handleFeedback(message, "negative")}
                        disabled={feedbackBusyId === message.id}
                        title="Mark this answer as not useful"
                      >
                        <ThumbsDown className="thumb-icon" size={13} />
                        Not useful
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}

          {sending && (
            <article className="message-row assistant">
              <div className="message-meta">
                <span className="assistant-label">
                  <img src={aiChatIcon} alt="" />
                  AI Chat Assistant
                </span>
              </div>
              <div className="message-bubble pending">
                <Loader2 className="spin" size={16} />
                {sendingWithMemory
                  ? "Thinking with thread context"
                  : "Thinking without thread context"}
              </div>
            </article>
          )}
          {requestError && !sending && (
            <>
              <article className="message-row user">
                <div className="message-meta"><span>You</span></div>
                <div className="message-bubble">
                  {capitalizeQuestion(requestError.question)}
                </div>
              </article>
              <article className="message-row assistant" role="alert">
                <div className="message-meta">
                  <span className="assistant-label">
                    <img src={aiChatIcon} alt="" />
                    AI Chat Assistant
                  </span>
                </div>
                <div className="message-bubble request-error">{requestError.message}</div>
              </article>
            </>
          )}
          <div ref={endRef} />
        </section>

        <form className="composer" onSubmit={handleSubmit}>
          {(quotaMessage || normalizeTokenUsage(tokenUsage)?.available < 1) && (
            <div className="quota-message" role="alert">
              {quotaMessage || tokenExhaustedMessage}
            </div>
          )}
          <div className="question-input-shell">
            <textarea
              ref={questionInputRef}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              onFocus={() => setQuestionInputFocused(true)}
              onBlur={() => setQuestionInputFocused(false)}
              disabled={normalizeTokenUsage(tokenUsage)?.available < 1}
              aria-autocomplete="list"
              aria-controls="question-suggestions"
              aria-expanded={
                questionInputFocused &&
                (questionSuggestionsLoading || questionSuggestions.length > 0)
              }
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" && questionSuggestions.length) {
                  event.preventDefault();
                  setActiveQuestionSuggestion((current) =>
                    current < questionSuggestions.length - 1 ? current + 1 : 0
                  );
                  return;
                }
                if (event.key === "ArrowUp" && questionSuggestions.length) {
                  event.preventDefault();
                  setActiveQuestionSuggestion((current) =>
                    current > 0 ? current - 1 : questionSuggestions.length - 1
                  );
                  return;
                }
                if (event.key === "Escape") {
                  setQuestionSuggestions([]);
                  setActiveQuestionSuggestion(-1);
                  return;
                }
                if (
                  (event.key === "Enter" || event.key === "Tab") &&
                  !event.shiftKey &&
                  activeQuestionSuggestion >= 0 &&
                  questionSuggestions[activeQuestionSuggestion]
                ) {
                  event.preventDefault();
                  selectQuestionSuggestion(
                    questionSuggestions[activeQuestionSuggestion]
                  );
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={
                normalizeTokenUsage(tokenUsage)?.available < 1
                  ? "Token allowance exhausted"
                  : "Ask specific questions about lease details, invoices, rates, or utilization to get the most relevant answers."
              }
              rows={1}
            />
            {questionInputFocused &&
              (questionSuggestionsLoading || questionSuggestions.length > 0) && (
                <div
                  className="question-suggestions"
                  id="question-suggestions"
                  role="listbox"
                  aria-label="Suggested questions"
                >
                  {questionSuggestionsLoading && !questionSuggestions.length && (
                    <div className="question-suggestions-status">
                      Loading suggestions…
                    </div>
                  )}
                  {questionSuggestions.map((suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      role="option"
                      aria-selected={activeQuestionSuggestion === index}
                      className={`question-suggestion ${
                        activeQuestionSuggestion === index ? "active" : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActiveQuestionSuggestion(index)}
                      onClick={() => selectQuestionSuggestion(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
          </div>
          <button
            type="submit"
            disabled={
              !question.trim() ||
              sending ||
              normalizeTokenUsage(tokenUsage)?.available < 1
            }
            title="Send question"
          >
            {sending ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
          </button>
          <div className="composer-notice">
            AI-generated responses may be inaccurate. Verify important information. Do not enter
            passwords, payment-card information or unauthorised personal data.{" "}
            <a
              className="composer-notice-meta"
              href={AI_CONSENT_POLICY_LINKS.aiDataUse}
              target="_blank"
              rel="noopener noreferrer"
            >
              AI &amp; Data Use
            </a>{" "}
            <span className="composer-provider-label">
              Powered by{" "}
              {formatProviderDisplayName(
                consentPolicy?.provider_display_name ||
                  AI_CONSENT_PROVIDER.provider_display_name
              )}
            </span>
          </div>
        </form>
      </main>
      {canManageTokens ? (
        <RechargeModal
          open={rechargeOpen}
          usage={tokenUsage}
          packages={rechargePackages}
          requests={rechargeRequests}
          pagination={rechargePagination}
          confirmation={rechargeConfirmation}
          submitting={rechargeSubmitting}
          loading={rechargeLoading}
          error={rechargeError}
          cancellingRequestId={rechargeCancellingId}
          // TODO: set confirmDisabled={false} on full release (~2 months)
          confirmDisabled
          onConfirm={handleRechargeConfirm}
          onCancelRequest={handleCancelRechargeRequest}
          onPageChange={(page) =>
            loadRechargeHistory(
              page,
              rechargePagination.page_size || RECHARGE_DEFAULT_PAGE_SIZE
            )
          }
          onPageSizeChange={(pageSize) => loadRechargeHistory(1, pageSize)}
          onClose={() => setRechargeOpen(false)}
        />
      ) : null}
      {feedbackCommentTarget ? (
        <FeedbackCommentModal
          key={feedbackCommentTarget.id}
          submitting={feedbackBusyId === feedbackCommentTarget.id}
          initialComments={getFeedbackComments(feedbackCommentTarget)}
          onSubmit={handleNegativeFeedbackSubmit}
          onClose={() => setFeedbackCommentTarget(null)}
        />
      ) : null}
      </div>
      {unauthorized && <UnauthorizedOverlay />}
    </div>
  );
}

export default RoleBaseAccess(App, {
  application: "lease_revamp",
  feature: "ai_chat_bot",
  operationType: ["R"]
});
